/** What the teacher is told about a chat channel, in numbers (STORY-124; FR-049, FR-055).
 *
 * One quiet line under the hero, and only while there is something to say: "N students are
 * still answering in Telegram" while any window on that channel is open, on whichever face
 * the stage is showing, gone on the first frame where the count is zero — so "within one
 * broadcast of the last window closing" is structural, not timed. Closing a round while
 * windows are open shows no warning, no confirm and no nag: the line persists and states the
 * fact. A channel that is down replaces its answering line with the failure line, and the
 * open round's unsupported item gets its own sentence. It names the channel and never a
 * student: every input is a count from the staff frame's `channels` block.
 *
 * The visible list is not a live region: it is mounted only while non-empty and moves with
 * every frame, which a screen reader would either ignore (content that arrives with a new
 * region) or read out once per Telegram answer. What is announced is `useChannelAnnouncement`,
 * fed to a live region the stage keeps mounted on every face: the first sentence at once,
 * then at most once per `ANNOUNCE_GAP_MS` while the numbers move, and silence when the
 * lines clear, so a stale count is never read after the fact.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { channelLines, type ChannelLineItem } from "./channels";
import type { Channels } from "./useLive";

type Props = { channels: Channels | undefined; roundOpen: boolean; upcoming?: boolean };

/** How often a moving count is re-announced, at most. Dozens of answers can land inside
 * one 45 s window; a listener needs the fact, not every increment. */
export const ANNOUNCE_GAP_MS = 5000;

/** The sentence for one line, as the teacher reads it. */
export function lineText(t: TFunction, l: ChannelLineItem): string {
  const name = t(`live.channel.names.${l.channel}`, { defaultValue: l.channel });
  if (l.kind === "answering") return t("live.channel.answering", { count: l.count ?? 0, channel: name });
  if (l.kind === "cannotAnswer") return t("live.channel.cannotAnswer", { count: l.count ?? 0, channel: name });
  if (l.kind === "unreachableNext")
    return l.count === null ? t("live.channel.unreachableNextUnknown", { channel: name }) : t("live.channel.unreachableNext", { count: l.count, channel: name });
  return l.count === null ? t("live.channel.unreachableUnknown", { channel: name }) : t("live.channel.unreachable", { count: l.count, channel: name });
}

/** When the next announcement may be spoken: at once for a first sentence or after a
 * silence, otherwise once the gap since the last one has passed. Pure, for the test. */
export function announceDelay(lastAt: number, now: number, gap = ANNOUNCE_GAP_MS): number {
  return Math.max(0, lastAt + gap - now);
}

/** The throttled text for the stage's polite live region: the current lines joined into one
 * utterance, updated at most once per gap, cleared at once when there is nothing to say. */
export function useChannelAnnouncement(channels: Channels | undefined, roundOpen: boolean, upcoming = false): string {
  const { t } = useTranslation();
  const text = channelLines(channels, roundOpen, upcoming)
    .map((l) => lineText(t, l))
    .join(". ");
  const [said, setSaid] = useState("");
  const lastAt = useRef(0);
  useEffect(() => {
    if (text === "") {
      setSaid("");
      return;
    }
    const id = setTimeout(() => {
      lastAt.current = Date.now();
      setSaid(text);
    }, announceDelay(lastAt.current, Date.now()));
    return () => clearTimeout(id);
  }, [text]);
  return said;
}

export function ChannelLine({ channels, roundOpen, upcoming = false }: Props) {
  const { t } = useTranslation();
  const lines = channelLines(channels, roundOpen, upcoming);
  if (lines.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 text-sm text-[color:var(--ink-2)]" data-channel-line>
      {lines.map((l) => (
        <li key={`${l.kind}:${l.channel}`} data-channel-line-kind={l.kind} className="flex items-baseline gap-2">
          <span aria-hidden className="shrink-0">{l.kind === "answering" ? "…" : "!"}</span>
          <span className="min-w-0">{lineText(t, l)}</span>
        </li>
      ))}
    </ul>
  );
}
